import type {
  HttpErrorResponse,
  HttpEvent,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import type { Observable } from 'rxjs';
import { catchError, from, switchMap, throwError } from 'rxjs';

import { AUTH_CONFIG } from './auth-config.token';
import { AuthService } from './auth.service';
import { AuthStore } from './auth.store';

/**
 * Functional HTTP interceptor for both apps.
 *
 * Responsibilities:
 *   1. Attach `Authorization: Bearer <accessToken>` to every outgoing
 *      request when a token is present.
 *   2. Set `withCredentials: true` so the refresh cookie rides on auth
 *      endpoint calls (the rest is harmless — backend ignores it).
 *   3. On 401 from a non-auth endpoint, try a single refresh; if it
 *      succeeds, replay the original request; if it fails, clear the
 *      store and bounce to /login.
 *
 * The single-flight refresh isn't queue-shared across concurrent 401s
 * yet — for the volumes of this app (low single-digit concurrent calls
 * per page) two parallel refreshes just rotate the cookie twice and
 * the second one wins. Revisit if/when we see refresh-rotation churn.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const store = inject(AuthStore);
  const auth = inject(AuthService);
  const config = inject(AUTH_CONFIG);
  const router = inject(Router);

  // Presigned object-storage URLs (S3 / MinIO) authenticate themselves via
  // their `X-Amz-*` query signature. Attaching our `Authorization: Bearer`
  // header — or `withCredentials` — makes the storage backend see two auth
  // schemes and reject the request (MinIO returns 400), which silently breaks
  // attachment uploads and, with them, photo thumbnails. Leave these requests
  // completely untouched.
  if (isPresignedStorageUrl(req.url)) {
    return next(req);
  }

  const isAuthRoute = isAuthEndpoint(req.url, config.refreshPath, config.loginPath);
  const withAuthHeader = attachAuth(req, store.accessToken());

  return next(withAuthHeader).pipe(
    catchError((err: HttpErrorResponse): Observable<HttpEvent<unknown>> => {
      if (err.status !== 401 || isAuthRoute) {
        return throwError(() => err);
      }
      return from(auth.bootRefresh()).pipe(
        switchMap(() => {
          const fresh = store.accessToken();
          if (!fresh) {
            void router.navigateByUrl(config.loginRoute);
            return throwError(() => err);
          }
          return next(attachAuth(req, fresh));
        }),
      );
    }),
  );
};

function attachAuth(req: HttpRequest<unknown>, token: string | null): HttpRequest<unknown> {
  const setHeaders: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
  return req.clone({
    setHeaders,
    withCredentials: true,
  });
}

/**
 * True for AWS SigV4 presigned URLs (S3 / MinIO upload + download links).
 * These embed their own signature in the query string, so our bearer token
 * and cookies must not be attached.
 */
function isPresignedStorageUrl(url: string): boolean {
  return url.includes('X-Amz-Signature') || url.includes('X-Amz-Algorithm');
}

function isAuthEndpoint(url: string, refreshPath: string, loginPath: string): boolean {
  return (
    url === refreshPath ||
    url === loginPath ||
    url.endsWith('/auth/login') ||
    url.endsWith('/auth/refresh')
  );
}

/**
 * Helper called from `bootstrapApplication` (or `app.config.ts`) via
 * `provideAppInitializer(authBoot)`. Kicks off the one-time `/auth/refresh`
 * probe but does NOT block app bootstrap on it — returning the promise here
 * would stall the first paint behind a full network round-trip (a major LCP
 * regression, worse on a cold backend).
 *
 * Correctness is unaffected: the store stays `loading`, and the route guards
 * (`authMatchGuard` / `loginRedirectGuard`) `await bootRefresh()` — the same
 * shared in-flight promise — before resolving a route. So the app shell /
 * splash paints immediately while auth settles in the background, and no
 * route renders against an unsettled session.
 */
export function authBoot(): () => void {
  return () => {
    const auth = inject(AuthService);
    void auth.bootRefresh();
  };
}
